import PropTypes from "prop-types"
import React from "react"
import { formatDate } from "src/main/Billing/Items/helpers"

import Heading from "src/components/Heading"

import Address from "./Address"
import Footer, { contactBoatProps } from "./Footer"
import ItemsTable, { itemProps } from "./ItemsTable"
import Summary, { summaryProps } from "./Summary"

const InvoicePrintView = ({
  invoice,
  installmentInvoice,
  contact,
  marina,
  transaction,
  summary,
  footer,
  items,
}) => {
  const renderInvoiceStatus = () => {
    return (
      <div data-testid="invoice-status" className="flex flex-col">
        <div className="flex flex-row space-x-2">
          <div className="font-bold">DUE</div>
          <div>{formatDate(invoice.dueDate)}</div>
        </div>
        <div className="flex flex-row space-x-2">
          <div className="font-bold">STATUS</div>
          <div>
            {invoice.displayStatus}
            {transaction
              ? ` ${transaction.transactedOn} with ${transaction.transactionMethod}`
              : null}
          </div>
        </div>
      </div>
    )
  }

  const headerText = invoice.balance === 0 ? "RECEIPT" : "INVOICE"

  return (
    <div className="hidden print:block" data-testid="invoice-print-view">
      <div className="mt-6 flex flex-col space-y-4">
        <Heading.PageTitle>{headerText}</Heading.PageTitle>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col space-y-4">
            <div data-testid="invoice-id">
              <div className="font-bold">ID</div>
              <div>{invoice.id}</div>
            </div>
            {renderInvoiceStatus()}
            <div data-testid="contact-address">
              <div className="font-bold">CUSTOMER</div>
              {contact ? (
                <Address
                  {...contact}
                  showEmail={Boolean(contact.email)}
                  showPhone={Boolean(contact.displayPhone)}
                />
              ) : (
                <div>Guest</div>
              )}
            </div>
          </div>
          <div className="flex flex-col space-y-4">
            {marina.logoUrl ? (
              <div className="flex w-[200px] justify-end">
                <img src={marina.logoUrl} alt={`${marina.name} logo`} />
              </div>
            ) : null}
            <div className="text-right" data-testid="marina-address">
              <Address
                {...marina.address}
                {...marina}
                showPhone={Boolean(marina.displayPhone)}
              />
            </div>
            {summary.appliedTaxRegistrations &&
              summary.appliedTaxRegistrations.length > 0 && (
                <div
                  className="text-right text-sm text-gray-500"
                  data-testid="tax-registrations"
                >
                  {summary.appliedTaxRegistrations.map((taxReg) => (
                    <div key={taxReg.displayName}>
                      {taxReg.displayName} # {taxReg.identifier}
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
      <ItemsTable items={items} installmentInvoice={installmentInvoice} />
      <div className="flex w-full justify-end border-t">
        <Summary summary={summary} installmentInvoice={installmentInvoice} />
      </div>
      <Footer
        sourceType={invoice.sourceType}
        sourceId={invoice.sourceId}
        {...footer}
      />
    </div>
  )
}

export const invoiceProps = {
  id: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  balance: PropTypes.number.isRequired,
  dueDate: PropTypes.string.isRequired,
  settledAtLocalTime: PropTypes.string,
  displayStatus: PropTypes.string.isRequired,
  autoSettleLastError: PropTypes.string,
  nextAutoSettleTime: PropTypes.string,
  paymentMethodId: PropTypes.string,
  sourceId: PropTypes.string.isRequired,
  sourcePath: PropTypes.string,
  sourceType: PropTypes.string.isRequired,
  paymentMethod: PropTypes.object,
}

export const contactProps = {
  email: PropTypes.string,
  name: PropTypes.string.isRequired,
  addressLineOne: PropTypes.string,
  addressLineTwo: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  displayPhone: PropTypes.string,
}

export const marinaProps = {
  name: PropTypes.string.isRequired,
  displayPhone: PropTypes.string,
  address: PropTypes.shape({
    addressLineOne: PropTypes.string,
    addressLineTwo: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string,
  }),
  logoUrl: PropTypes.string,
}

const transactionProps = {
  transactionMethod: PropTypes.string,
  transactedOn: PropTypes.string,
}

InvoicePrintView.propTypes = {
  invoice: PropTypes.shape(invoiceProps).isRequired,
  installmentInvoice: PropTypes.bool.isRequired,
  contact: PropTypes.shape(contactProps),
  marina: PropTypes.shape(marinaProps).isRequired,
  transaction: PropTypes.shape(transactionProps),
  summary: PropTypes.shape(summaryProps).isRequired,
  footer: PropTypes.shape({
    contactBoats: PropTypes.arrayOf(PropTypes.shape(contactBoatProps)),
    sourceDates: PropTypes.string,
    sourceTotal: PropTypes.number,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape(itemProps)).isRequired,
}

export default InvoicePrintView
